import React from 'react';
import styled from 'styled-components';
import theme from '../theme';

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${theme.spacing.md};
  margin-bottom: ${theme.spacing.xl};

  ${theme.media.mobile} {
    grid-template-columns: 1fr;
  }
`;

const StatCard = styled.div`
  background: ${theme.colors.surface};
  border-radius: ${theme.radii.md};
  box-shadow: ${theme.shadows.sm};
  padding: ${theme.spacing.lg};
  display: flex;
  align-items: center;
  gap: ${theme.spacing.md};
  cursor: ${({ $clickable }) => $clickable ? 'pointer' : 'default'};
  transition: all ${theme.transitions.fast};

  &:hover {
    ${({ $clickable }) => $clickable ? `box-shadow: ${theme.shadows.md}; transform: translateY(-1px);` : ''}
  }

  ${theme.media.mobile} {
    padding: ${theme.spacing.md};
  }
`;

const StatIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: ${theme.radii.md};
  background: ${({ $bg }) => $bg || theme.colors.primaryGhost};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
  flex-shrink: 0;
`;

const StatContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const StatValue = styled.div`
  font-family: ${theme.typography.fontFamily};
  font-size: 22px;
  font-weight: 700;
  color: ${theme.colors.text};
  line-height: 1.2;
`;

const StatLabel = styled.div`
  font-family: ${theme.typography.fontFamily};
  font-size: ${theme.typography.small.size};
  color: ${theme.colors.textSecondary};
  margin-top: 2px;
`;

const SetGoalHint = styled.div`
  font-family: ${theme.typography.fontFamily};
  font-size: ${theme.typography.tiny.size};
  color: ${theme.colors.primary};
  margin-top: 2px;
  font-weight: 600;
`;

const StatsOverview = ({ totalSessions, currentStreak, weeklyGoalPct, canSetGoals, onSetGoals }) => {
  const stats = [
    {
      icon: '\u{1F3AF}',
      bg: theme.colors.primaryGhost,
      value: totalSessions,
      label: 'Total Sessions',
    },
    {
      icon: '\u{1F525}',
      bg: 'rgba(255, 149, 0, 0.1)',
      value: currentStreak > 0 ? `${currentStreak} wk` : '—',
      label: 'Current Streak',
    },
    {
      icon: '\u{1F4C5}',
      bg: theme.colors.successLight,
      value: weeklyGoalPct !== null ? `${weeklyGoalPct}%` : '—',
      label: 'Weekly Goal',
      clickable: canSetGoals,
      onClick: onSetGoals,
      hint: canSetGoals && weeklyGoalPct === null ? 'Click to set goal' : null,
    },
  ];

  return (
    <StatsGrid data-tour="stats-overview">
      {stats.map((stat) => (
        <StatCard key={stat.label} $clickable={stat.clickable} onClick={stat.onClick}>
          <StatIcon $bg={stat.bg}>{stat.icon}</StatIcon>
          <StatContent>
            <StatValue>{stat.value}</StatValue>
            <StatLabel>{stat.label}</StatLabel>
            {stat.hint && <SetGoalHint>{stat.hint}</SetGoalHint>}
          </StatContent>
        </StatCard>
      ))}
    </StatsGrid>
  );
};

export default StatsOverview;
